import React, { useState, useEffect } from 'react';
import { Alert, Typography } from '@one-thd/sui-atomic-components';
import {
  CardNumberInput,
  CardPinInput
} from '@thd-olt-component-react/gift-cards-check-balance-and-reload';
import { useDrawer } from '../../hooks/use-drawer';
import { useGiftCardRequest } from '../../hooks/use-gift-card-request';
import { RESTRICTED_ADD_TO_ACCOUNT_PROGRAM, imgUrl } from '../../utils/consts';
import { ISaveButtonProps } from '../../models/common-models';
import { setOverlayEvent, setButtonEvent, setErrorEvent } from '../../utils/analytics';
import { formatMaskedCardNumber } from '../../utils/general';

interface ICardInfoData {
  paymentValue?: string;
  pin?: string;
  imagePath?: string;
}
interface ILegacyGiftCardEditProps {
  cardLast4: string;
  paymentId: string;
  onUpdated: () => void;
}

export const GiftCardEditLegacy: React.FC<ILegacyGiftCardEditProps> = ({
  cardLast4,
  paymentId,
  onUpdated
}) => {
  useEffect(() => {
    setOverlayEvent('update legacy');
  }, []);

  const { makeRequest: updateCard } = useGiftCardRequest({
    queryName: 'updateGiftCardV2'
  });
  const [error, setError] = useState('');

  const [cardInfo, setCardInfo] = useState<ICardInfoData>({
    paymentValue: '',
    pin: '',
    imagePath: `${imgUrl.pathname}giftcard_crate.png`
  });
  const { closeDrawer, updateDrawerState } = useDrawer();

  const handleOnCardNumberReady = (cardNumber: string) => {
    const _newState = {
      ...cardInfo,
      paymentValue: cardNumber
    };
    setCardInfo(_newState);
    readyToSave(_newState);
  };

  const handleOnPinReady = (pin: string) => {
    const _newState = {
      ...cardInfo,
      pin
    };
    setCardInfo(_newState);
    readyToSave(_newState);
  };

  const updateSaveButtonProps = (saveProps: ISaveButtonProps) => {
    const newDrawerState = {
      saveChangesProps: saveProps,
      ContentComponent: GiftCardEditLegacy
    };
    updateDrawerState(newDrawerState);
  };

  const handleError = (errMsg: string) => {
    updateSaveButtonProps({
      loading: false,
      onClick: undefined
    });
    setError(errMsg);
  };

  const updateLegacyGC = () => {
    updateSaveButtonProps({ loading: true });
    const variables = {
      giftCard: {
        id: paymentId,
        ...cardInfo
      }
    };

    updateCard(variables)
      .then((_data) => {
        if (_data?.paymentCards[0]?.saveToAccountStatus) {
          onUpdated();
          closeDrawer();
          setButtonEvent('cards and accounts: updated legacy gift card');
        }
      })
      .catch((_err) => {
        setErrorEvent('update legacy');
        const errorMessage = _err?.errorMessage;
        handleError(errorMessage);
      });
  };

  const readyToSave = (_newState: ICardInfoData) => {
    const isReady = _newState?.paymentValue && _newState?.pin;
    updateSaveButtonProps({
      onClick: isReady ? updateLegacyGC : undefined
    });
  };

  return (
    <div className="sui-flex sui-flex-col sui-gap-4">
      <Alert data-testid="alert-edit-legacy" status="info">
        <div>
          <Typography variant="body-base">
            Easily check your gift card balance, reload with just a few taps, and seamlessly apply
            gift cards at checkout.
          </Typography>
          <div className="sui-w-full sui-py-3">
            <Typography variant="body-base" weight="bold">
              Re-enter your card number and PIN to access these features.
            </Typography>
          </div>
          <Typography variant="body-base">
            {`You'll still be able to review all purchase history associated with this gift card in
            your account.`}
          </Typography>
        </div>
      </Alert>
      <div className="sui-wu-full sui-py-4 sui-flex sui-items-center">
        <img
          className="sui-rounded-md sui-pr-2"
          src={`${imgUrl.host}${cardInfo.imagePath}`}
          alt="best seller img"
          height="45"
          width="80"
          data-testid="legacy-gift-card-image"
        />
        <Typography color="subtle">{formatMaskedCardNumber(cardLast4)}</Typography>
      </div>
      <CardNumberInput
        onReady={handleOnCardNumberReady}
        restrictedProgramCodes={RESTRICTED_ADD_TO_ACCOUNT_PROGRAM}
      />
      <CardPinInput onReady={handleOnPinReady} />
      {error && <Alert status="error">{error}</Alert>}
    </div>
  );
};
